import React, { useEffect, useRef, useState } from "react";
import { api, fmtUSD } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Send, Zap, Database, Trash2, Route } from "lucide-react";
import { toast } from "sonner";

const MODES = ["auto", "balanced", "cost", "intelligence", "latency"];

function RouteBadges({ m }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2 text-[10px]">
      <span className="px-1.5 py-0.5 rounded-sm font-semibold text-white cs-mono" style={{ backgroundColor: m.model.color }}>
        {m.model.name}
      </span>
      <span className="px-1.5 py-0.5 rounded-sm bg-[#e0f2fe] text-[#0369a1] font-semibold">{m.intent}</span>
      <span className="px-1.5 py-0.5 rounded-sm bg-secondary cs-mono">
        {m.mode}{m.mode === "auto" ? `→${m.effective_mode}` : ""}
      </span>
      {m.cache?.hit ? (
        <span className="px-1.5 py-0.5 rounded-sm bg-[#dcfce7] text-[#15803d] font-semibold" data-testid="cache-hit-badge">
          CACHE HIT {(m.cache.similarity * 100).toFixed(1)}% · saved {fmtUSD(m.cache.saved_usd)}
        </span>
      ) : (
        <span className="px-1.5 py-0.5 rounded-sm bg-[#fef3c7] text-[#92400e] font-semibold" data-testid="cache-miss-badge">CACHE MISS</span>
      )}
      <span className="cs-mono text-muted-foreground">
        {m.tokens_in}→{m.tokens_out} tok · {fmtUSD(m.cost_usd)} · {m.latency_ms}ms{m.source === "fallback" ? " · simulated" : ""}
      </span>
    </div>
  );
}

export default function TMPlaygroundPanel() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("auto");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cacheStats, setCacheStats] = useState(null);
  const [lastRoute, setLastRoute] = useState(null);
  const endRef = useRef(null);

  const loadCache = () => api.tmPlayground.cache().then(setCacheStats).catch(console.error);
  useEffect(() => { loadCache(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const r = await api.tmPlayground.chat(sessionId, text, mode);
      setSessionId(r.session_id);
      setMessages((m) => [...m, { role: "assistant", content: r.reply, meta: r }]);
      setLastRoute(r);
      loadCache();
    } catch (e) {
      toast.error("Routing failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex gap-4" data-testid="tm-playground-panel">
      {/* Chat */}
      <div className="flex-1 min-w-0 cs-card flex flex-col" style={{ height: 560 }}>
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <Route className="w-4 h-4 text-[#00579e]" />
          <div>
            <div className="cs-heading text-sm font-semibold">Routing Playground</div>
            <div className="text-[10px] text-muted-foreground">each reply is intent-classified, routed, cached and billed independently</div>
          </div>
          <button onClick={() => { setMessages([]); setSessionId(null); setLastRoute(null); }}
            data-testid="playground-new-session" className="ml-auto text-[11px] text-[#00579e] hover:underline">new session</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="playground-messages">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
              <p className="text-sm">Start a conversation — Dex picks the model automatically.</p>
              <p className="text-[11px] cs-mono mt-1">try: "summarize our GCP waste" · "write a SQL query for spend by team" · then repeat one to see a cache hit</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`max-w-[85%] ${m.role === "user" ? "ml-auto" : ""}`}>
              <div className={`rounded-sm px-3 py-2 text-xs whitespace-pre-wrap ${m.role === "user" ? "bg-[#00579e] text-white" : "bg-[hsl(var(--cs-tint))] border border-border"}`}
                   data-testid={`playground-msg-${m.role}-${i}`}>
                {m.content}
              </div>
              {m.meta && <RouteBadges m={m.meta} />}
            </div>
          ))}
          {busy && <div className="text-[11px] text-muted-foreground animate-pulse" data-testid="playground-thinking">routing & generating…</div>}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-border">
          <div className="flex gap-1.5 mb-2">
            {MODES.map((md) => (
              <button key={md} onClick={() => setMode(md)} data-testid={`playground-mode-${md}`}
                className={`px-2.5 py-1 rounded-full text-[11px] cs-mono border ${mode === md ? "bg-[#00579e] text-white border-transparent" : "bg-white border-border hover:bg-[hsl(var(--cs-tint))]"}`}>
                {md}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Say something…" data-testid="playground-input" className="rounded-sm h-9 text-xs" />
            <Button onClick={send} disabled={busy || !input.trim()} data-testid="playground-send"
              className="rounded-sm h-9 bg-[#00579e] hover:bg-[#004580] text-white px-4">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Route trace + cache */}
      <aside className="w-[300px] shrink-0 space-y-4">
        <div className="cs-card p-4" data-testid="route-trace-card">
          <div className="flex items-center gap-1.5 mb-2">
            <Zap className="w-3.5 h-3.5 text-[#00579e]" />
            <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Last route trace</span>
          </div>
          {!lastRoute ? (
            <p className="text-[11px] text-muted-foreground">Send a message to see how it was routed.</p>
          ) : (
            <div className="space-y-2 text-[11px]">
              <p className="text-muted-foreground leading-relaxed">{lastRoute.reason}</p>
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground pt-1">Candidates considered</div>
              {lastRoute.candidates.map((c, i) => (
                <div key={c.id} className={`flex items-center gap-2 ${i === 0 ? "font-semibold" : "text-muted-foreground"}`}>
                  <span className="cs-mono w-4">{i + 1}.</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="cs-mono">q{c.quality} · {c.score.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="cs-card p-4" data-testid="cache-stats-card">
          <div className="flex items-center gap-1.5 mb-2">
            <Database className="w-3.5 h-3.5 text-[#15803d]" />
            <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Semantic cache</span>
            <button onClick={async () => { await api.tmPlayground.clearCache(); loadCache(); toast.success("Cache cleared"); }}
              data-testid="clear-cache-button" className="ml-auto"><Trash2 className="w-3 h-3 text-[#b91c1c]" /></button>
          </div>
          {cacheStats && (
            <>
              <div className="grid grid-cols-3 gap-2 text-center mb-2">
                {[["Entries", cacheStats.entries], ["Hits", cacheStats.total_hits], ["Hit rate", `${cacheStats.hit_rate_pct}%`]].map(([l, v]) => (
                  <div key={l}>
                    <div className="cs-mono text-sm font-semibold" data-testid={`cache-${l.toLowerCase().replace(" ", "-")}`}>{v}</div>
                    <div className="text-[9px] uppercase tracking-[0.12em] text-muted-foreground">{l}</div>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-muted-foreground mb-1.5">similarity ≥ {(cacheStats.threshold * 100).toFixed(0)}% serves from cache</div>
              {cacheStats.recent.slice(0, 6).map((e) => (
                <div key={e.id} className="border-t border-border/60 py-1.5 text-[10px]">
                  <div className="truncate">{e.prompt}</div>
                  <div className="cs-mono text-muted-foreground">{e.model_name} · {e.hits} hit{e.hits === 1 ? "" : "s"}</div>
                </div>
              ))}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
